import { useEffect, useRef, useState } from "react";
import { MessageResponse } from "@/components/ai-elements/message";
import type { StreamdownProps } from "streamdown";
import { snapPastMarker } from "./citationPlugin";

interface SmoothMessageResponseProps {
  content: string;
  className?: string;
  /**
   * If true, the component will animate the text typewriting.
   * If false, it will display the content immediately.
   */
  shouldAnimate: boolean;
  components?: StreamdownProps["components"];
  rehypePlugins?: StreamdownProps["rehypePlugins"];
}

export const SmoothMessageResponse = ({
  content,
  className,
  shouldAnimate,
  components,
  rehypePlugins,
}: SmoothMessageResponseProps) => {
  // We initialize with content if not animating, or empty/partial if animating.
  // However, to be safe and avoid flash, if we are animating but mounting for the first time
  // with a lot of text, we probably want to start from 0 or catch up.
  // Given the "streaming" context, we usually start with small text.
  const [displayedContent, setDisplayedContent] = useState(
    shouldAnimate ? "" : content
  );

  const displayedContentRef = useRef(displayedContent);
  const animationFrameRef = useRef<number | null>(null);
  const lastUpdateRef = useRef<number>(0);

  // Sync ref
  useEffect(() => {
    displayedContentRef.current = displayedContent;
  }, [displayedContent]);

  useEffect(() => {
    // If we shouldn't animate, or if the content is completely fully displayed, stop doing anything.
    if (!shouldAnimate) {
      if (displayedContent !== content) {
        setDisplayedContent(content);
        displayedContentRef.current = content;
      }
      return;
    }

    // If we should animate, but we are already done or ahead (weird case), snap.
    if (displayedContent.length >= content.length) {
      if (displayedContent !== content) {
        // Content might have changed/shrunk? Just snap.
        setDisplayedContent(content);
        displayedContentRef.current = content;
      }
      return;
    }

    // Animation Logic
    const animate = (timestamp: number) => {
      const current = displayedContentRef.current;
      const target = content;

      if (current.length >= target.length) {
        // Double check exact match
        if (current !== target) {
          setDisplayedContent(target);
          displayedContentRef.current = target;
        }
        return;
      }

      // If content changed drastically (e.g. we were at "A", now target is "A long text"),
      // we don't want to type it one by one forever.
      // But for streaming, it's usually incremental.

      // Determine how many characters to add.
      // We want a typewriter effect.
      // Let's try to add characters based on time delta to ensure consistent speed,
      // but also speed up if we are falling behind.

      const charsToAdd = Math.max(
        1,
        Math.floor((target.length - current.length) / 10)
      ); // Heuristic: if far behind, add more.

      let next = target.slice(0, current.length + charsToAdd);
      // Don't reveal citation markers partially — snap past them
      next = snapPastMarker(next, target);

      setDisplayedContent(next);
      displayedContentRef.current = next;

      // Schedule next frame
      // We can throttle this to control speed if needed, but requestAnimationFrame is usually 60fps.
      // 60 chars per second is okay, maybe a bit fast.
      // Let's use setTimeout-ish behavior via checking timestamp if we want slower.
      // But "immediate" snap was requested for cancellation, so speed during typing is fine to be fast.

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [content, shouldAnimate, displayedContent]); // Dependencies: if content changes, we re-evaluate loop.

  return (
    <MessageResponse
      className={className}
      components={components}
      rehypePlugins={rehypePlugins}
    >
      {displayedContent}
    </MessageResponse>
  );
};
